import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Shape, ShapeType } from '../../model/types';

export interface PolygonBounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

export const getRectArea = (widthPx: number, heightPx: number, pixelsPerMeter: number): number => {
  return (widthPx / pixelsPerMeter) * (heightPx / pixelsPerMeter);
};

export const getPolygonArea = (points: number[], pixelsPerMeter: number): number => {
  const pointsCount = points.length / 2;
  if (pointsCount < 3) return 0;

  let signedAreaPx = 0;
  for (let i = 0; i < pointsCount; i++) {
    const x1 = points[2 * i];
    const y1 = points[2 * i + 1];
    const x2 = points[2 * ((i + 1) % pointsCount)];
    const y2 = points[2 * ((i + 1) % pointsCount) + 1];
    signedAreaPx += x1 * y2 - x2 * y1;
  }

  const areaPx = Math.abs(signedAreaPx) / 2;
  return areaPx / (pixelsPerMeter * pixelsPerMeter);
};

export const getPolygonBounds = (points: number[]): PolygonBounds => {
  if (points.length < 2) {
    return { minX: 0, minY: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    if (points[i] < minX) minX = points[i];
    if (points[i] > maxX) maxX = points[i];
    if (points[i + 1] < minY) minY = points[i + 1];
    if (points[i + 1] > maxY) maxY = points[i + 1];
  }

  return { minX, minY, width: maxX - minX, height: maxY - minY };
};

export const formatArea = (squareMeters: number): string => {
  return `${roundNumber(squareMeters)} m²`;
};

export interface PolygonExtremes {
  maxX: number;
  maxY: number;
}

/**
 * Square footprint of any shape, expressed in m². Returns 0 for shapes that
 * have no meaningful 2D area (line, text). Honours `scaleX`/`scaleY` so that
 * un-baked scale transforms don't under-count the area.
 */
export const getShapeSquareMeters = (shape: Shape, pixelsPerMeter: number): number => {
  switch (shape.type) {
    case ShapeType.RECTANGLE: {
      const widthPx = shape.width * (shape.scaleX ?? 1);
      const heightPx = shape.height * (shape.scaleY ?? 1);
      return getRectArea(widthPx, heightPx, pixelsPerMeter);
    }
    case ShapeType.CIRCLE: {
      const radiusXPx = shape.radiusX * (shape.scaleX ?? 1);
      const radiusYPx = shape.radiusY * (shape.scaleY ?? 1);
      const areaPx = Math.PI * radiusXPx * radiusYPx;
      return areaPx / (pixelsPerMeter * pixelsPerMeter);
    }
    case ShapeType.POLYGON:
    case ShapeType.POLYGON_BOX:
      return getPolygonArea(shape.points, pixelsPerMeter);
    default:
      return 0;
  }
};

export const getPolygonExtremes = (points: number[]): PolygonExtremes => {
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < points.length; i += 2) {
    if (points[i] > maxX) maxX = points[i];
    if (points[i + 1] > maxY) maxY = points[i + 1];
  }

  if (!Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return { maxX: 0, maxY: 0 };
  }

  return { maxX, maxY };
};
